/**
 * Import from Smartsheet — the UI for `POST /api/btp/agreements/import`.
 *
 * The importer shipped in #1839 and has never been run against staging, because
 * running it meant crafting an authenticated request by hand. This screen is the
 * whole of what was missing: pick a file, read the dry-run diff, press commit.
 *
 * ── Why the preview is not optional ──────────────────────────────────────────
 * The route's default is a dry run and `commit=true` is the only way past it —
 * 2,670 rows, one transaction, no undo. This screen keeps that shape rather than
 * flattening it into one button, so the numbers on screen are always a plan
 * somebody has looked at. There is no code path from choosing a file to a write.
 *
 * ── Read the bytes ONCE ──────────────────────────────────────────────────────
 * A `File` handle reads from disk lazily, and the route takes no `run_id` — it
 * re-plans from whatever bytes the commit call carries. So a re-read at commit
 * time could import a file that was edited after the preview, and the diff the
 * user approved would describe a plan that never ran. The bytes are read into an
 * ArrayBuffer at pick time and both calls send that same buffer.
 *
 * ── The rejects lead ────────────────────────────────────────────────────────
 * Every reject is an identification problem — a row nothing can be matched to,
 * not bad data — so it is the only part of the report someone can act on. When
 * there are any, the row filter opens on them. The alternative (a separate
 * always-visible reject list above a full table) renders the same rows twice,
 * which is the drift item 69 documented.
 */
import { useMemo, useRef, useState } from 'react';
import { Link } from 'react-router';
import { apiFetch, ApiError } from '@/features/crm/lib/api-fetch';
import { useMe } from '@/features/crm/auth/use-capabilities';
import {
  APPLIED_LABELS,
  MAX_IMPORT_BYTES,
  PLANNED_LABELS,
  assertCommitted,
  commitImport,
  describeCommit,
  fmt,
  normaliseCounts,
  openingFilter,
  previewImport,
  writesNothing,
  type AppliedRow,
  type CommitResponse,
  type Counts,
  type PlannedOutcome,
  type PreviewResponse,
  type PreviewRow,
} from '@/features/hotel-supplier/lib/smartsheet-import';

/** The bytes, held so the commit cannot re-read the file. */
interface Picked {
  name: string;
  size: number;
  bytes: ArrayBuffer;
}

type RowFilter = PlannedOutcome | 'all';

const FILTERS: { id: RowFilter; label: string }[] = [
  { id: 'all', label: 'All rows' },
  { id: 'reject', label: 'Cannot be imported' },
  { id: 'create', label: 'New' },
  { id: 'update', label: 'Updated' },
  { id: 'skip', label: 'Left alone' },
];

/** Plan outcome → the applied outcome that came from it, for the filter to
 *  keep working on the post-commit table. `skip` has no applied row. */
const APPLIED_OF: Record<PlannedOutcome, string> = {
  create: 'created',
  update: 'updated',
  reject: 'rejected',
  skip: '—',
};

const MATCH_LABELS: Record<string, string> = {
  row_id: 'Smartsheet row id',
  negotiation_id: 'Negotiation id',
  composite: 'Account + property + term',
  none: 'no match',
};

export default function ImportAgreementsPage() {
  const { can, loading: capsLoading } = useMe();

  const [picked, setPicked] = useState<Picked | null>(null);
  const [preview, setPreview] = useState<PreviewResponse | null>(null);
  const [result, setResult] = useState<CommitResponse | null>(null);
  const [busy, setBusy] = useState<'preview' | 'commit' | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState<RowFilter>('all');
  const inputRef = useRef<HTMLInputElement>(null);

  /*
   * EVERY HOOK ABOVE EVERY EARLY RETURN.
   *
   * This memo used to sit below the capability gate, and that was a real crash,
   * not a lint preference: `useMe` resolves asynchronously, so the first render
   * is read-only, takes the early return, and runs eight hooks — then
   * capabilities arrive, the gate opens, and the ninth hook appears. React:
   * "Rendered more hooks than during the previous render", and the page is a
   * blank screen with a console error.
   *
   * It survived the render tests because their `useMe` mock answers the same
   * value on every render, so the transition they were meant to cover never
   * happened. `does not crash when capabilities arrive after the first render`
   * is the test that actually reproduces it.
   */
  const counts: Counts | null = useMemo(
    () =>
      result
        ? normaliseCounts(result)
        : preview
          ? normaliseCounts(preview)
          : null,
    [preview, result],
  );

  // Distinct from read-only: nobody knows yet. Claiming "your access is
  // read-only" while the answer is still in flight is a false statement that
  // then silently replaces itself.
  if (capsLoading) {
    return (
      <div className="page" data-testid="supplier-import-page">
        <div className="loading-state" role="status">
          Checking your access…
        </div>
      </div>
    );
  }

  if (!can('write:supplier')) {
    return (
      <div className="page" data-testid="supplier-import-page">
        <div className="page-header">
          <h1>Import from Smartsheet</h1>
        </div>
        <div className="banner" data-testid="supplier-import-readonly">
          Importing creates and updates agreements, so it needs write access to
          Supplier Management. Yours is read-only.
        </div>
      </div>
    );
  }

  /** Everything after a file is chosen, discarded when one is re-chosen. */
  function reset() {
    setPreview(null);
    setResult(null);
    setError(null);
    setFilter('all');
  }

  async function choose(file: File | undefined) {
    if (!file || busy) return;
    // A NEW FILE INVALIDATES THE OLD PLAN. Without this, a preview of file A
    // stays on screen beside a commit button that would send file B.
    setPicked(null);
    reset();

    if (file.size === 0) {
      setError('That file is empty.');
      return;
    }
    if (file.size > MAX_IMPORT_BYTES) {
      setError(
        `That file is ${Math.round(file.size / (1024 * 1024))} MB — the limit is ` +
          `${MAX_IMPORT_BYTES / (1024 * 1024)} MB. The All Stages export is about 2 MB, ` +
          'so this is probably not it.',
      );
      return;
    }

    const bytes = await file.arrayBuffer();
    const next: Picked = { name: file.name, size: file.size, bytes };
    setPicked(next);
    setBusy('preview');
    try {
      const res = await previewImport(bytes, next.name, apiFetch);
      setPreview(res);
      // Open on the rows somebody has to do something about — chosen from the
      // rows actually listed, not the counts. See `openingFilter`.
      setFilter(openingFilter(res.rows, 'reject'));
    } catch (err) {
      setError(messageOf(err, 'The preview could not be produced.'));
    } finally {
      setBusy(null);
    }
  }

  async function commit() {
    if (!picked || !preview || busy) return;
    setBusy('commit');
    setError(null);
    try {
      // `assertCommitted` is the guard, not a formality: a commit that comes
      // back as a dry run is a 200 with real numbers and an unchanged database.
      const res = assertCommitted(
        await commitImport(picked.bytes, picked.name, apiFetch),
      );
      setResult(res);
      /*
       * RE-EVALUATE THE FILTER AGAINST THE RESULT, not the plan.
       *
       * The list opens on whatever is actionable, and after a write that is the
       * rows the write itself rejected — which is not the same set the plan
       * rejected. Leaving the plan's filter in place showed "No rows in this
       * group" immediately after a successful import of 2,016 agreements, which
       * reads as the import having done nothing.
       */
      setFilter(openingFilter(res.rows, 'rejected'));
    } catch (err) {
      setError(
        messageOf(
          err,
          'The import failed. Nothing was written — the whole run is rolled back.',
        ),
      );
    } finally {
      setBusy(null);
    }
  }

  return (
    <div className="page" data-testid="supplier-import-page">
      <div className="page-header">
        <Link className="page-back" to="/hotel-supplier/agreements">
          ← Agreements
        </Link>
        <h1>Import from Smartsheet</h1>
        <p className="page-intro" style={{ marginBottom: 0 }}>
          Export <strong>All Stages</strong> from Smartsheet as CSV and drop it
          here. Nothing is written until you have read the preview and pressed
          import.
        </p>
      </div>

      {/* ── The file ─────────────────────────────────────────────────────── */}
      <div className="section" data-testid="supplier-import-file-section">
        <div className="section-header">
          <span className="section-label">The file</span>
          {picked && (
            <span
              className="section-meta"
              data-testid="supplier-import-filename"
            >
              {picked.name} · {(picked.size / 1024).toFixed(0)} KB
            </span>
          )}
        </div>
        <div className="section-body">
          <input
            accept=".csv,text/csv"
            data-testid="supplier-import-file-input"
            disabled={busy !== null}
            onChange={(e) => void choose(e.target.files?.[0])}
            ref={inputRef}
            style={{ display: 'none' }}
            type="file"
          />
          <button
            className={
              preview || result ? 'btn btn-outline' : 'btn btn-primary'
            }
            data-testid="supplier-import-choose"
            disabled={busy !== null}
            onClick={() => inputRef.current?.click()}
            type="button"
          >
            {picked ? 'Choose a different file' : 'Choose the CSV'}
          </button>
          <p className="page-intro" style={{ marginBottom: 0, marginTop: 12 }}>
            It must be <strong>CSV</strong>, not XLSX — the spreadsheet format
            cannot be read here. The first line has to be the sheet’s header
            row.
          </p>
        </div>
      </div>

      {busy === 'preview' && (
        <div
          className="loading-state"
          data-testid="supplier-import-previewing"
          role="status"
        >
          Reading the sheet and working out what would change…
        </div>
      )}

      {error && (
        <div
          className="banner banner-error"
          data-testid="supplier-import-error"
          role="alert"
        >
          {error}
        </div>
      )}

      {/* ── The commit, and the promise it makes ─────────────────────────── */}
      {preview && !result && (
        <div className="section" data-testid="supplier-import-commit-section">
          <div className="section-header">
            <span className="section-label">Import it</span>
            <span className="section-meta">
              {fmt(preview.rows_in_file)} rows read · preview {preview.run_id}
            </span>
          </div>
          <div className="section-body">
            {busy === 'commit' ? (
              <div
                className="loading-state"
                data-testid="supplier-import-committing"
                role="status"
              >
                Importing. This runs as <strong>one transaction</strong> and can
                take up to two minutes on the full sheet — please don’t close
                the tab. If it fails, nothing at all is written.
              </div>
            ) : (
              <>
                <button
                  className="btn btn-primary"
                  data-testid="supplier-import-commit"
                  disabled={writesNothing(counts!)}
                  onClick={() => void commit()}
                  type="button"
                >
                  {describeCommit(counts!)}
                </button>
                <p
                  className="page-intro"
                  data-testid="supplier-import-commit-note"
                  style={{ marginBottom: 0, marginTop: 12 }}
                >
                  {writesNothing(counts!)
                    ? 'This sheet would change nothing, so there is nothing to import.'
                    : 'One transaction: it all lands or none of it does. There is no undo.'}
                  {counts!.reject > 0 && (
                    <>
                      {' '}
                      The {fmt(counts!.reject)} row
                      {counts!.reject === 1 ? '' : 's'} that cannot be imported
                      {counts!.reject === 1 ? ' is' : ' are'} skipped, not
                      guessed at.
                    </>
                  )}
                </p>
              </>
            )}
          </div>
        </div>
      )}

      {/* ── What happened ────────────────────────────────────────────────── */}
      {result && (
        <div className="section" data-testid="supplier-import-done">
          <div className="section-header">
            <span className="section-label">Imported</span>
            <span className="section-meta">run {result.run_id}</span>
          </div>
          <div className="section-body">
            <p className="page-intro" style={{ marginTop: 0 }}>
              {fmt(counts!.create)} agreement
              {counts!.create === 1 ? '' : 's'} created and{' '}
              {fmt(counts!.update)} updated, from {fmt(result.rows_in_file)}{' '}
              rows in {picked?.name}.
            </p>
            {/* `.kv-grid` takes `.kv-row` > `.kv-label` + `.kv-value` — a
                `<dl>` of dt/dd is not that vocabulary and renders unstyled. */}
            <div className="kv-grid" data-testid="supplier-import-side-effects">
              <SideEffect
                label="Properties created"
                note="hotels the sheet named that the CRM had no record of"
                value={result.properties_created}
              />
              <SideEffect
                label="Contacts seeded"
                note="from the sheet’s contacted-by / date / method columns"
                value={result.contacts_seeded}
              />
              <SideEffect
                label="Rate audits seeded"
                note="from the tested-date / result columns"
                value={result.rate_tests_seeded}
              />
            </div>
            <Link className="btn btn-primary" to="/hotel-supplier/agreements">
              Open Agreements
            </Link>
          </div>
        </div>
      )}

      {/* ── The report. Same shape before and after. ─────────────────────── */}
      {counts && (
        <>
          <div
            className="kpi-row kpi-row-4"
            data-testid="supplier-import-counts"
          >
            {(['create', 'update', 'skip', 'reject'] as PlannedOutcome[]).map(
              (k) => (
                <div className="kpi-card" key={k}>
                  <div className="kpi-label">
                    {result ? APPLIED_LABELS[k] : PLANNED_LABELS[k]}
                  </div>
                  <div
                    className="kpi-value"
                    data-testid={`supplier-import-count-${k}`}
                  >
                    {fmt(counts[k])}
                  </div>
                </div>
              ),
            )}
          </div>

          <Notes
            statuses={(result ?? preview)!.unknown_statuses}
            unmapped={preview?.unmapped_headers ?? []}
            warnings={(result ?? preview)!.warnings}
          />

          <RowTable
            appliedRows={result?.rows ?? null}
            filter={filter}
            onFilter={setFilter}
            previewRows={preview?.rows ?? []}
            truncated={(result ?? preview)!.rows_truncated}
          />
        </>
      )}
    </div>
  );
}

function SideEffect({
  label,
  note,
  value,
}: {
  label: string;
  note: string;
  value: number;
}) {
  return (
    <div className="kv-row">
      <span className="kv-label">{label}</span>
      <span className="kv-value">
        <span className="num">{fmt(value)}</span>{' '}
        <span className="muted">— {note}</span>
      </span>
    </div>
  );
}

/**
 * Warnings, unknown statuses and unmapped headers.
 *
 * None of these is an error and none blocks the import, which is exactly why
 * they need saying: an unknown SR Status is a picklist someone edited in
 * Smartsheet, and an unmapped header is a column we are dropping on the floor.
 * Both are things only the person holding the sheet can explain.
 */
function Notes({
  statuses,
  unmapped,
  warnings,
}: {
  statuses: string[];
  unmapped: string[];
  warnings: string[];
}) {
  if (!warnings.length && !statuses.length && !unmapped.length) return null;
  return (
    <div className="section" data-testid="supplier-import-notes">
      <div className="section-header">
        <span className="section-label">Worth reading</span>
      </div>
      <div className="section-body">
        {warnings.length > 0 && (
          <ul data-testid="supplier-import-warnings" style={{ marginTop: 0 }}>
            {warnings.map((w) => (
              <li key={w}>{w}</li>
            ))}
          </ul>
        )}
        {statuses.length > 0 && (
          <p
            className="page-intro"
            data-testid="supplier-import-unknown-statuses"
            style={{ marginTop: 0 }}
          >
            <strong>SR Status values we don’t recognise:</strong>{' '}
            {statuses.join(', ')}. Not an error — it usually means the picklist
            was edited in Smartsheet. Those rows import, but the status is left
            as it was.
          </p>
        )}
        {unmapped.length > 0 && (
          <p
            className="page-intro"
            data-testid="supplier-import-unmapped"
            style={{ marginTop: 0, marginBottom: 0 }}
          >
            <strong>Columns not imported,</strong> because there is nowhere to
            put them yet: {unmapped.join(', ')}.
          </p>
        )}
      </div>
    </div>
  );
}

function RowTable({
  appliedRows,
  filter,
  onFilter,
  previewRows,
  truncated,
}: {
  appliedRows: AppliedRow[] | null;
  filter: RowFilter;
  onFilter: (f: RowFilter) => void;
  previewRows: PreviewRow[];
  truncated: number;
}) {
  /*
   * AFTER a commit the rows are the APPLIED rows — created/updated/rejected,
   * with the agreement id the write produced. Before, they are the plan. Same
   * table, and the outcome column changes tense with it.
   */
  const rows = useMemo(() => {
    if (appliedRows) {
      return appliedRows
        .filter(
          (r) =>
            filter === 'all' ||
            r.outcome === APPLIED_OF[filter as PlannedOutcome],
        )
        .map((r) => ({
          line: r.line,
          outcome: r.outcome,
          match: null as string | null,
          agreementId: r.agreementId ?? null,
          reasons: r.reasons,
        }));
    }
    return previewRows
      .filter((r) => filter === 'all' || r.outcome === filter)
      .map((r) => ({
        line: r.line,
        outcome: r.outcome,
        match: r.match as string | null,
        agreementId: null,
        reasons: r.reasons,
      }));
  }, [appliedRows, filter, previewRows]);

  const total = appliedRows ? appliedRows.length : previewRows.length;

  return (
    <div className="section" data-testid="supplier-import-rows-section">
      <div className="section-header" style={{ flexWrap: 'wrap' }}>
        <div
          style={{
            display: 'flex',
            flexWrap: 'wrap',
            flex: '1 1 auto',
            gap: 6,
          }}
        >
          {FILTERS.map((f) => (
            <button
              className={'pill' + (filter === f.id ? ' active' : '')}
              data-active={filter === f.id ? 'true' : 'false'}
              data-testid={`supplier-import-filter-${f.id}`}
              key={f.id}
              onClick={() => onFilter(f.id)}
              type="button"
            >
              {f.label}
            </button>
          ))}
        </div>
        <span className="section-meta" data-testid="supplier-import-rows-meta">
          {fmt(rows.length)} of {fmt(total)} listed
          {truncated > 0 && ` · ${fmt(truncated)} more not listed`}
        </span>
      </div>
      <div className="section-body">
        {truncated > 0 && (
          <p
            className="table-note"
            data-testid="supplier-import-truncated"
            style={{ marginTop: 0 }}
          >
            The report lists the first 200 rows. {fmt(truncated)} more were
            {appliedRows ? ' imported' : ' planned'} and are not shown — the
            counts above cover all of them.
          </p>
        )}
        {rows.length === 0 ? (
          <div
            className="empty-inline"
            data-testid="supplier-import-rows-empty"
          >
            No rows in this group.
          </div>
        ) : (
          <div className="table-scroll">
            <table className="data-table">
              <thead>
                <tr>
                  <th scope="col">Sheet line</th>
                  <th scope="col">Outcome</th>
                  <th scope="col">
                    {appliedRows ? 'Agreement' : 'Matched on'}
                  </th>
                  <th scope="col">Why</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr
                    data-testid={`supplier-import-row-${r.line}`}
                    key={r.line}
                  >
                    <td className="num">{r.line}</td>
                    <td>{r.outcome}</td>
                    <td>
                      {r.agreementId ? (
                        <Link
                          className="link"
                          to={`/hotel-supplier/agreements/${r.agreementId}`}
                        >
                          {r.agreementId}
                        </Link>
                      ) : (
                        <span className="muted">
                          {r.match ? (MATCH_LABELS[r.match] ?? r.match) : '—'}
                        </span>
                      )}
                    </td>
                    <td>{r.reasons.join('; ')}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

/** The API's own message wherever it gave one — it is the authority on what was
 *  wrong, and its wording for the import failure is better than any generic. */
function messageOf(err: unknown, fallback: string): string {
  if (err instanceof ApiError) {
    const body = err.body as {
      error?: string;
      message?: string;
      failure_class?: string | null;
      failure_code?: string | null;
      failure_at?: string | null;
    } | null;
    const text = body?.error ?? body?.message ?? err.message;
    /*
     * SHOW WHAT FAILED, not just that something did.
     *
     * The banner used to end at the ref, and reading the log behind that ref
     * needs `az` — so the person looking at the screen could not tell a
     * transaction timeout from a unique-constraint violation, and neither
     * could anyone they sent a screenshot to. Seven attempts went that way on
     * staging.
     *
     * The API returns only the error CLASS and CODE, both allowlisted to an
     * identifier shape. Neither can carry a client name or a rate; the message
     * that can is still server-side only.
     */
    const detail = [body?.failure_class, body?.failure_code, body?.failure_at]
      .filter((v): v is string => typeof v === 'string' && v.length > 0)
      .join(' · ');
    return detail ? `${text} [${detail}]` : text;
  }
  if (err instanceof Error) return err.message;
  return fallback;
}
